/**
 * Lightweight observability layer.
 *
 * Wraps error tracking, performance marks, and analytics events behind a
 * stable interface so the project can swap in Sentry / PostHog / Datadog
 * without touching call sites.
 *
 * In production this no-ops gracefully when no provider is configured.
 */

type Severity = "info" | "warning" | "error" | "fatal";

interface BreadcrumbOptions {
  category?: string;
  data?: Record<string, unknown>;
  level?: Severity;
}

interface ErrorContext {
  user?: { id?: string; email?: string };
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  severity?: Severity;
}

const isBrowser = typeof window !== "undefined";
const isProd = isBrowser && import.meta.env?.PROD === true;

const breadcrumbs: Array<BreadcrumbOptions & { ts: number; message: string }> = [];
const MAX_BREADCRUMBS = 50;

export function addBreadcrumb(message: string, options: BreadcrumbOptions = {}) {
  breadcrumbs.push({ ...options, message, ts: Date.now() });
  if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift();
}

export function getBreadcrumbs() {
  return [...breadcrumbs];
}

export function clearBreadcrumbs() {
  breadcrumbs.length = 0;
}

export function captureError(error: unknown, context: ErrorContext = {}) {
  const err = error instanceof Error ? error : new Error(String(error));
  const payload = {
    message: err.message,
    stack: err.stack,
    severity: context.severity ?? "error",
    user: context.user,
    tags: context.tags,
    extra: context.extra,
    breadcrumbs: getBreadcrumbs(),
    url: isBrowser ? window.location.href : undefined,
    ts: new Date().toISOString(),
  };

  if (!isProd) {
    console.error("[observability]", payload);
  }

  // Ship to the production monitoring pipeline (no-ops outside prod).
  import("@/lib/errorMonitoring")
    .then(({ reportError }) =>
      reportError({
        message: payload.message,
        stack: payload.stack ?? null,
        severity: payload.severity,
        source: (context.tags?.source as string) ?? "captureError",
        tags: context.tags,
        extra: context.extra,
        breadcrumbs: payload.breadcrumbs,
      }),
    )
    .catch(() => {
      /* monitoring must never break the app */
    });

  return payload;

}

export function captureMessage(message: string, severity: Severity = "info") {
  const payload = { message, severity, breadcrumbs: getBreadcrumbs(), ts: new Date().toISOString() };
  if (!isProd) {
    console.info("[observability]", payload);
  }
  return payload;
}

/** Track a user-facing event (page view, feature used, conversion). */
export function trackEvent(name: string, properties: Record<string, unknown> = {}) {
  const payload = { name, properties, ts: Date.now() };
  if (!isProd) {
    console.debug("[event]", name, properties);
  }
  // Hook point: posthog.capture(name, properties)
  return payload;
}

/** Mark + measure a performance span. */
export function startSpan(label: string) {
  const start = isBrowser && performance ? performance.now() : Date.now();
  return {
    end(extra: Record<string, unknown> = {}) {
      const end = isBrowser && performance ? performance.now() : Date.now();
      const duration = end - start;
      addBreadcrumb(`span:${label}`, { category: "perf", data: { duration_ms: duration, ...extra } });
      return duration;
    },
  };
}

/**
 * Named marketing-funnel events. Centralised so call sites stay typo-free
 * and so we can later map them to a real analytics provider in one place.
 */
export const MARKETING_EVENTS = {
  heroPromiseSeen: "hero_promise_seen",
  simulatorCompleted: "simulator_completed",
  simulatorToSignup: "simulator_to_signup",
  clinicianLeadCaptured: "clinician_lead_captured",
  exitIntentShown: "exit_intent_shown",
  ctaClick: "cta_click",
  ctaImpression: "cta_impression",
  signupStarted: "signup_started",
  waitlistSubmitted: "waitlist_submitted",
} as const;

/**
 * Track a *viewport* impression for a CTA — fired only after the element has
 * been visible (≥50%) for a dwell period. Distinct from `experiment_exposure`
 * (which fires on mount) so we can compute honest CTR from real impressions.
 */
export function trackCtaImpression(
  location: string,
  label: string,
  opts: { experiments?: Record<string, string>; variant_label?: string } = {},
) {
  const payload = {
    location,
    label,
    experiments: opts.experiments,
    variant_label: opts.variant_label,
  };
  trackEvent(MARKETING_EVENTS.ctaImpression, payload);
  // Persist to funnel_events so the admin dashboard can aggregate it.
  import("@/lib/funnelAnalytics")
    .then(({ trackFunnelEvent }) =>
      (trackFunnelEvent as unknown as (n: string, m: Record<string, unknown>) => void)(
        "cta_impression",
        payload,
      ),
    )
    .catch(() => {
      /* ignore */
    });
}

/**
 * Track a landing-page CTA click. Fires a lightweight local event and,
 * when `signup` is true, also logs a `signup_started` funnel event so
 * anonymous pre-signup clicks can be stitched to a later trial_started.
 */
export function trackCtaClick(
  location: string,
  label: string,
  opts: { signup?: boolean; destination?: string; experiments?: Record<string, string> } = {},
) {
  // Merge in any active sticky experiment assignments so every click carries
  // its variant context for A/B analysis.
  let experiments = opts.experiments;
  if (!experiments) {
    try {
      // Async, best-effort. Keeps observability.ts free of an experiment import cycle.
      import("@/lib/experiment").then(({ getAllAssignments }) => {
        const all = getAllAssignments();
        if (Object.keys(all).length) experiments = all;
      });
    } catch {
      /* ignore */
    }
    // For the *first* call before the dynamic import resolves, best-effort
    // synchronous read from localStorage using the same prefix.
    try {
      const out: Record<string, string> = {};
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith("myelina.exp.")) continue;
        const v = localStorage.getItem(k);
        if (v) out[k.slice("myelina.exp.".length)] = v;
      }
      if (Object.keys(out).length) experiments = out;
    } catch {
      /* ignore */
    }
  }
  trackEvent(MARKETING_EVENTS.ctaClick, {
    location,
    label,
    destination: opts.destination,
    experiments,
  });
  if (opts.signup) {
    // Fire-and-forget; funnelAnalytics never throws.
    import("@/lib/funnelAnalytics")
      .then(({ trackFunnelEvent }) =>
        trackFunnelEvent("signup_started", {
          location,
          label,
          destination: opts.destination,
          experiments,
        }),
      )
      .catch(() => {
        /* ignore */
      });
  }
}

export const observability = {
  addBreadcrumb,
  captureError,
  captureMessage,
  trackEvent,
  startSpan,
  getBreadcrumbs,
  clearBreadcrumbs,
};
